import jsPDF from 'jspdf';
import 'jspdf-autotable';

export const generateInvoicePDF = (invoice, company, senderInfo) => {
  const doc = new jsPDF();
  
  // Colors - Professional Navy & Gray
  const primaryColor = [30, 64, 175]; // Blue-800
  const secondaryColor = [30, 41, 59]; // Slate-800
  const grayColor = [100, 116, 139]; // Slate-500
  const lightGray = [241, 245, 249]; // Slate-100

  // --- HEADER ---
  doc.setFillColor(...primaryColor);
  doc.rect(0, 0, 210, 40, 'F');
  
  doc.setFontSize(28);
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.text("FAKTURA", 196, 25, { align: 'right' });

  doc.setFontSize(22);
  doc.text("HMSVarsling", 14, 25);
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.text("En tjeneste fra Employer Brand AS", 14, 32);

  // --- SENDER & RECIPIENT INFO ---
  let yPos = 55;
  
  // Recipient (Kunde)
  doc.setFontSize(10);
  doc.setTextColor(...secondaryColor);
  doc.setFont("helvetica", "bold");
  doc.text("FAKTURA TIL:", 14, yPos);
  
  yPos += 7;
  doc.setFontSize(12);
  doc.text(company?.name || "Ukjent Bedrift", 14, yPos);
  
  yPos += 6;
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text(`Org.nr: ${company?.org_number || company?.orgNumber || '-'}`, 14, yPos);
  
  if (company?.raw_data?.address) {
    yPos += 5;
    doc.text(company.raw_data.address, 14, yPos);
  }
  
  // Sender (HMSVarsling) - Right side
  let senderY = 62;
  const xRight = 130;
  doc.setFont("helvetica", "bold");
  doc.text(senderInfo.name, xRight, senderY);
  doc.setFont("helvetica", "normal");
  doc.text(senderInfo.address, xRight, senderY + 5);
  doc.text(`${senderInfo.zip} ${senderInfo.city}`, xRight, senderY + 10);
  doc.text(`Org.nr: ${senderInfo.orgNr}`, xRight, senderY + 15);
  doc.text(`E-post: ${senderInfo.email}`, xRight, senderY + 20);

  // --- INVOICE METADATA BOX ---
  const detailY = 100;
  doc.setFillColor(...lightGray);
  doc.rect(14, detailY - 5, 182, 20, 'F');
  
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...grayColor);
  doc.text("FAKTURANR", 20, detailY);
  doc.text("DATO", 60, detailY);
  doc.text("FORFALL", 100, detailY);
  doc.text("BETALINGSBETINGELSER", 145, detailY);

  doc.setFontSize(10);
  doc.setTextColor(...secondaryColor);
  doc.text(String(invoice.invoice_number || invoice.invoiceNumber || 'NY'), 20, detailY + 8);
  doc.text(new Date(invoice.date || invoice.created_at).toLocaleDateString('nb-NO'), 60, detailY + 8);
  doc.text(new Date(invoice.due_date || invoice.dueDate).toLocaleDateString('nb-NO'), 100, detailY + 8);
  doc.text("14 dager netto", 145, detailY + 8);

  // --- ITEMS TABLE ---
  const tableColumn = ["Beskrivelse", "Antall", "Pris eks. mva", "MVA", "Beløp"];
  const tableRows = [];

  const items = Array.isArray(invoice.items) ? invoice.items : [];
  items.forEach(item => {
    const itemData = [
      item.description,
      item.quantity || 1,
      `${(item.price || 0).toLocaleString('no-NO')},-`,
      `${item.vatRate || 25}%`,
      `${((item.price || 0) * (item.quantity || 1)).toLocaleString('no-NO')},-`
    ];
    tableRows.push(itemData);
  });

  doc.autoTable({
    startY: 125,
    head: [tableColumn],
    body: tableRows,
    theme: 'striped',
    headStyles: { fillColor: primaryColor, fontSize: 9, fontStyle: 'bold' },
    styles: { fontSize: 10, cellPadding: 5 },
    columnStyles: { 
      0: { cellWidth: 80 }, 
      4: { halign: 'right', fontStyle: 'bold' } 
    }
  });

  // --- TOTALS ---
  const finalY = doc.lastAutoTable.finalY + 15;
  const totalX = 140;
  
  doc.setFontSize(10);
  doc.setTextColor(...grayColor);
  doc.text("Netto beløp:", totalX, finalY);
  doc.setTextColor(...secondaryColor);
  doc.text(`${(invoice.net_amount || invoice.netAmount || 0).toLocaleString('no-NO')},-`, 195, finalY, { align: 'right' });

  doc.setTextColor(...grayColor);
  doc.text("MVA (25%):", totalX, finalY + 7);
  doc.setTextColor(...secondaryColor);
  doc.text(`${(invoice.vat_amount || invoice.vatAmount || 0).toLocaleString('no-NO')},-`, 195, finalY + 7, { align: 'right' });

  doc.setFillColor(...primaryColor);
  doc.rect(totalX - 5, finalY + 12, 65, 12, 'F');
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(255, 255, 255);
  doc.text("TOTALT Å BETALE:", totalX, finalY + 20);
  doc.text(`${(invoice.total_amount || invoice.totalAmount || 0).toLocaleString('no-NO')},-`, 195, finalY + 20, { align: 'right' });

  // --- PAYMENT INFO FOOTER ---
  const bottomY = 260;
  doc.setDrawColor(200);
  doc.line(14, bottomY, 196, bottomY);
  
  doc.setFontSize(9);
  doc.setTextColor(...grayColor);
  doc.setFont("helvetica", "bold");
  doc.text("BETALINGSINFORMASJON", 14, bottomY + 8);
  
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...secondaryColor);
  doc.text(`Bankkonto: ${senderInfo.bankAccount}`, 14, bottomY + 14);
  doc.text(`KID-nummer: ${invoice.kid || 'Ikke oppgitt'}`, 80, bottomY + 14);
  doc.text(`Forfallsdato: ${new Date(invoice.due_date || invoice.dueDate).toLocaleDateString('nb-NO')}`, 145, bottomY + 14);

  doc.save(`Faktura_${invoice.invoice_number || 'draft'}.pdf`);
};